import { createSelector } from "reselect";

import type { RootState } from "~/app/store";
import { isBundleQuoteGroup, isProductGroup } from "~/features/cart/cart/models/CartData";
import type {
  BundleQuoteGroup,
  EntryGroup,
  OrderableGroup,
  ProductGroup,
  QuotableGroup,
} from "~/features/cart/cart/dto/CartResponseDTO";
import type { LineItem } from "~/features/cart/cart/models/CartData";
import { selectLineItemsByGroupNumber } from "~/features/cart/cart/redux/selectors/cartSelectors";
import {
  selectBundleGroups,
  selectOrderableGroup,
  selectQuotableGroup,
} from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";
import { buildAlternateRows, type StoredAlternateRow } from "~/features/cart/cartV2/redux/cartAlternatesSlice";
import {
  buildProductTableViewModel,
  buildOptionalPartDescriptorMap,
} from "~/features/cart/cartV2/utils/buildProductTableViewModel";
import { selectResolvedProductGroup } from "~/features/cart/cartV2/utils/selectResolvedGroupedEntryV2";
import type { ProductTableViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";

const selectEntryGroupsByNumber = (state: RootState) => state.cart.entryGroupsByNumber;
const selectProductsState = (state: RootState) => state.products;
const selectCartAlternatesState = (state: RootState) => state.cartAlternates;

function buildSelectorState(productsState: RootState["products"]): RootState {
  return { products: productsState } as RootState;
}

function collectProductGroups(group: EntryGroup): ProductGroup[] {
  if (isProductGroup(group)) {
    return [group];
  }
  return group.children.flatMap(collectProductGroups);
}

function buildGroupTableId(group: EntryGroup): string {
  const quoteNumber = isBundleQuoteGroup(group) ? group.quoteNumber : "";
  return `table:${group.groupType}:${group.groupNumber}:${quoteNumber}`;
}

function buildTableViewModelFromGroup(args: {
  group: OrderableGroup | QuotableGroup | BundleQuoteGroup;
  mode: "edit" | "review";
  selectorState: RootState;
  storedRows: StoredAlternateRow[];
  entryGroupsByNumber: RootState["cart"]["entryGroupsByNumber"];
  lineItemsByGroupNumber: Record<number, LineItem[]>;
}): ProductTableViewModel {
  const { group, mode, selectorState, storedRows, entryGroupsByNumber, lineItemsByGroupNumber } = args;

  const tableId = buildGroupTableId(group);
  const productGroups = collectProductGroups(group);
  const resolvedProductGroups = productGroups.map((pg) =>
    selectResolvedProductGroup(pg, lineItemsByGroupNumber)(selectorState),
  );

  const productGroupNumbers = productGroups.map((pg) => pg.groupNumber);
  const alternateRows = buildAlternateRows({ tableId, storedRows, productGroupNumbers });

  const optionalPartDescriptors = buildOptionalPartDescriptorMap(selectorState, resolvedProductGroups);

  return buildProductTableViewModel({
    group,
    mode,
    resolvedProductGroups,
    lineItemsByGroupNumber,
    optionalPartDescriptors,
    alternateRows,
    entryGroupsByNumber,
  });
}

const emptyTableViewModel = (mode: "edit" | "review"): ProductTableViewModel => ({
  tableId: "table:empty",
  mode,
  groupType: "ORDERABLE" as ProductTableViewModel["groupType"],
  rows: [],
});

export const selectOrderablesViewModel = createSelector(
  selectOrderableGroup,
  selectEntryGroupsByNumber,
  selectProductsState,
  selectCartAlternatesState,
  selectLineItemsByGroupNumber,
  (
    orderableGroup,
    entryGroupsByNumber,
    productsState,
    cartAlternatesState,
    lineItemsByGroupNumber,
  ): ProductTableViewModel => {
    if (!orderableGroup) {
      return emptyTableViewModel("edit");
    }

    const tableId = buildGroupTableId(orderableGroup);
    const selectorState = buildSelectorState(productsState);
    const storedRows = cartAlternatesState.rowsByTableId[tableId] ?? [];

    return buildTableViewModelFromGroup({
      group: orderableGroup,
      mode: "edit",
      selectorState,
      storedRows,
      entryGroupsByNumber,
      lineItemsByGroupNumber,
    });
  },
);

export const selectCheckoutOrderablesViewModel = createSelector(
  selectOrderableGroup,
  selectEntryGroupsByNumber,
  selectProductsState,
  selectLineItemsByGroupNumber,
  (orderableGroup, entryGroupsByNumber, productsState, lineItemsByGroupNumber): ProductTableViewModel => {
    if (!orderableGroup) {
      return emptyTableViewModel("review");
    }

    const selectorState = buildSelectorState(productsState);

    return buildTableViewModelFromGroup({
      group: orderableGroup,
      mode: "review",
      selectorState,
      storedRows: [],
      entryGroupsByNumber,
      lineItemsByGroupNumber,
    });
  },
);

export const selectQuotablesViewModel = createSelector(
  selectQuotableGroup,
  selectEntryGroupsByNumber,
  selectProductsState,
  selectLineItemsByGroupNumber,
  (quotableGroup, entryGroupsByNumber, productsState, lineItemsByGroupNumber): ProductTableViewModel => {
    if (!quotableGroup) {
      return emptyTableViewModel("review");
    }

    const selectorState = buildSelectorState(productsState);

    return buildTableViewModelFromGroup({
      group: quotableGroup,
      mode: "review",
      selectorState,
      storedRows: [],
      entryGroupsByNumber,
      lineItemsByGroupNumber,
    });
  },
);

export const selectBundleViewModels = createSelector(
  selectBundleGroups,
  selectEntryGroupsByNumber,
  selectProductsState,
  selectLineItemsByGroupNumber,
  (bundleGroups, entryGroupsByNumber, productsState, lineItemsByGroupNumber): ProductTableViewModel[] => {
    const selectorState = buildSelectorState(productsState);

    return bundleGroups.map((bundle) =>
      buildTableViewModelFromGroup({
        group: bundle,
        mode: "edit",
        selectorState,
        storedRows: [],
        entryGroupsByNumber,
        lineItemsByGroupNumber,
      }),
    );
  },
);

export const selectCheckoutBundleViewModels = createSelector(
  selectBundleGroups,
  selectEntryGroupsByNumber,
  selectProductsState,
  selectLineItemsByGroupNumber,
  (bundleGroups, entryGroupsByNumber, productsState, lineItemsByGroupNumber): ProductTableViewModel[] => {
    const selectorState = buildSelectorState(productsState);

    return bundleGroups.map((bundle) =>
      buildTableViewModelFromGroup({
        group: bundle,
        mode: "review",
        selectorState,
        storedRows: [],
        entryGroupsByNumber,
        lineItemsByGroupNumber,
      }),
    );
  },
);
